import { html, body } from '@utilities/dom-elements';
import Events from '@utilities/events';

const CLICK_AREA_HOOK = '[js-hook-image-zoom-click-area]';
const IMAGE_WRAPPER_HOOK = '[js-hook-image-zoom-image-wrapper]';
const IMAGE_HOOK = '[js-hook-image]';
const HIGHRES_IMAGE_HOOK = '[js-hook-image-zoom-highres-image]';
const FULLSCREEN_BUTTON_HOOK = '[js-hook-image-zoom-full-screen-button]';
const CLOSE_BUTTON_HOOK = '[js-hook-image-zoom-close-button]';
const PREV_BUTTON_HOOK = '[js-hook-image-zoom-prev-button]';
const NEXT_BUTTON_HOOK = '[js-hook-image-zoom-next-button]';

const IS_TOUCH_CLASS = 'is-touch';
const ZOOMED_CLASS = 'image-zoom--zoomed';
const FULLSCREEN_CLASS = 'image-zoom--fullscreen';
const FULLSCREEN_BODY_CLASS = 'fl-zoom-open';
const LOADER_CLASS = 'has--loader';
const IS_IMAGE_FULL_SCREEN = 'is--image-full-screen';
const NOSCROLL_CLASS = 'no--scroll';

class ImageZoom {
  constructor(element) {
    this.element = element;
    this.clickArea = this.element.querySelector(CLICK_AREA_HOOK);
    this.imgWrapper = this.element.querySelector(IMAGE_WRAPPER_HOOK);
    this.lowresImage = this.element.querySelector(IMAGE_HOOK);
    this.highresImg = this.element.querySelector(HIGHRES_IMAGE_HOOK);
    this.fullscreenButton = this.element.querySelector(FULLSCREEN_BUTTON_HOOK);
    this.closeButton = this.element.querySelector(CLOSE_BUTTON_HOOK);
    this.prevButton = this.element.querySelector(PREV_BUTTON_HOOK);
    this.nextButton = this.element.querySelector(NEXT_BUTTON_HOOK);
    this.highresImgSrc = this.highresImg.dataset.src;
    this.zoom = this.element.dataset.zoom;
    this.fullscreenZoom = this.element.dataset.fullscreenZoom;

    this.isZoomedIn = false;
    this.isFullscreen = false;

    this.bindEvents();
  }

  bindEvents() {
    if (!html.classList.contains(IS_TOUCH_CLASS)) {
      this.clickArea.addEventListener('click', (e) => this.clickHandler(e));
      this.clickArea.addEventListener('mousemove', (e) => this.updateImagePosition(e));
      this.clickArea.addEventListener('mouseleave', (e) => this.resetZoom(e));
    }

    this.fullscreenButton.addEventListener('click', (e) => this.toggleFullScreen(e, true));
    this.closeButton.addEventListener('click', (e) => this.toggleFullScreen(e));

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        Events.$trigger('high-res-image-gallery::prev', this.element.id);
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        Events.$trigger('high-res-image-gallery::next', this.element.id);
      });
    }

    Events.$on(`zoom-image::show-highres-image-${this.element.id}`, () => this.toggleHighResImage(true, true));
    Events.$on(`zoom-image::hide-highres-image-${this.element.id}`, () => this.toggleHighResImage());
  }

  updateImagePosition(e) {
    if (!this.isZoomedIn) {
      this.imgWrapper.removeAttribute('style');
    } else {
      const zoomFactor = this.element.classList.contains(FULLSCREEN_CLASS) ? this.fullscreenZoom : this.zoom;

      const elRect = this.element.getBoundingClientRect();
      const elWidth = this.element.offsetWidth;
      const elHeight = this.element.offsetHeight;

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const posXpercentage = (mouseX - elRect.left) / elWidth * 100;
      const posYpercentage = (mouseY - elRect.top) / elHeight * 100;

      const factorX = -((posXpercentage - 50) * 0.02);
      const factorY = -((posYpercentage - 50) * 0.02);

      const maxMoveX = ((elWidth * zoomFactor) - elWidth) / 2;
      const maxMoveY = ((elHeight * zoomFactor) - elHeight) / 2;

      const transX = `${maxMoveX * factorX}px`;
      const transY = `${maxMoveY * factorY}px`;

      this.imgWrapper.style.transform = `translate3d(${transX}, ${transY}, 0px) scale(${zoomFactor})`;
    }
  }

  resetImagePosition() {
    this.isZoomedIn = false;
    this.imgWrapper.removeAttribute('style');
  }

  clickHandler(e) {
    if (!this.element.classList.contains(FULLSCREEN_CLASS)) {
      this.toggleHighResImage(!this.isZoomedIn);
    }

    this.element.classList[this.isZoomedIn ? 'remove' : 'add'](ZOOMED_CLASS);

    this.isZoomedIn = !this.isZoomedIn;

    this.updateImagePosition(e);
  }

  toggleHighResImage(show, isFullscreen) {
    if (isFullscreen) {
      this.isFullscreen = true;
    }

    this.element.classList[show && this.isFullscreen ? 'add' : 'remove'](LOADER_CLASS);
    body.classList[show && this.isFullscreen ? 'add' : 'remove'](IS_IMAGE_FULL_SCREEN);

    if (show) {
      this.highresImg.innerHTML = `<img alt="" src="${this.highresImgSrc}" js-hook-objectfit-img>`;
    }
    // specifically check for false, undefined should not remove image
    else if (show === false) {
      this.highresImg.innerHTML = '';
    }
  }

  resetZoom(e) {
    if (this.isZoomedIn) {
      this.clickHandler(e);
    }
  }

  toggleFullScreen(e, forceFullScreen) {
    this.resetImagePosition();

    if (!html.classList.contains(IS_TOUCH_CLASS)) {
      this.resetZoom(e);
    }

    this.isFullscreen = forceFullScreen ? true : !this.isFullscreen;

    this.element.classList[this.isFullscreen ? 'add' : 'remove'](FULLSCREEN_CLASS);
    html.classList[this.isFullscreen ? 'add' : 'remove'](NOSCROLL_CLASS);
    document.querySelector('body').classList[this.isFullscreen ? 'add' : 'remove'](FULLSCREEN_BODY_CLASS);

    if (this.isFullscreen) {
      this.lowresImage.querySelector('img').removeAttribute('sizes');
    }

    this.toggleHighResImage(this.isFullscreen);
  }
}

export default ImageZoom;
